import Close from "@mui/icons-material/Close";
import OpenInFull from "@mui/icons-material/OpenInFull";
import Remove from "@mui/icons-material/Remove";
import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useThemeGreyBackgroundColor } from "hooks/useThemeGreyBackgroundColor";
import { Link as RouterLink, useParams } from "react-router-dom";

import type { ScoutChatMessage, ScoutToolPart } from "../hooks/types";

import { useScoutConversation } from "../hooks/useScoutConversation";

export type MinimizedScoutBarProps = {
  conversationId: string | undefined;
  onExpand: () => void;
  onClose: () => void;
};

const BAR_WIDTH = 800;
const BAR_HEIGHT = 80;

/**
 * Picks the latest assistant tool call that hasn't produced output yet. The
 * tool part stream lands `output_available`/`output_error` once a tool
 * finishes — anything else means it's still working.
 */
function getActiveToolCall(messages: ScoutChatMessage[]): ScoutToolPart | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message || message.role !== "assistant") {
      continue;
    }
    for (let j = message.parts.length - 1; j >= 0; j -= 1) {
      const part = message.parts[j];
      if (!part || part.type !== "tool") {
        continue;
      }
      if (part.toolState === "input_streaming" || part.toolState === "input_available") {
        return part;
      }
      // Stop after the first tool seen on the last assistant message — older
      // ones are already resolved.
      return undefined;
    }
    return undefined;
  }
  return undefined;
}

/**
 * Docked, bottom-left rectangle shown when the user minimizes the Scout
 * popover. Surfaces an active tool call (if any) so the user knows what
 * Scout is doing. Clicking the body expands the popover; the close button
 * ends the conversation.
 */
export function MinimizedScoutBar({ conversationId, onExpand, onClose }: MinimizedScoutBarProps) {
  const theme = useTheme();
  const canvasColor = useThemeGreyBackgroundColor();
  const { tenantId } = useParams();
  const { messages, activeTurn } = useScoutConversation({
    tenantId,
    conversationId,
  });
  const activeToolCall = getActiveToolCall(messages);
  const isStreaming = activeTurn?.status === "starting" || activeTurn?.status === "streaming";
  const activeToolLabel = activeToolCall?.toolDisplayName ?? activeToolCall?.toolName;
  const fullPageHref =
    tenantId && conversationId ? `/o/${tenantId}/scout/${conversationId}` : undefined;

  return (
    <Box
      role="dialog"
      aria-label="Scout (minimized)"
      sx={{
        position: "fixed",
        bottom: 40,
        left: 40,
        zIndex: theme.zIndex.modal + 1,
        bgcolor: canvasColor,
        boxShadow: 6,
        borderRadius: "4px",
        border: 1,
        borderColor: "divider",
        width: BAR_WIDTH,
        height: BAR_HEIGHT,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ pl: 1.5, pr: 0.5, height: "100%" }}
      >
        <Box
          role="button"
          tabIndex={0}
          onClick={onExpand}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onExpand();
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
            height: "100%",
            outline: "none",
            "&:focus-visible": {
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
              borderRadius: 1,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1, lineHeight: 1, color: "text.primary" }}
          >
            SCOUT
          </Typography>
          {/*
            Matches the streaming indicator from the main chat (AgentMessage):
            the Scout logo itself spins while a turn is in flight. Keyed off
            `scoutSpin` so the keyframe survives any subtree re-mounts.
          */}
          <Box
            sx={{
              "@keyframes scoutSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
              display: "flex",
              alignItems: "center",
            }}
          >
            <ScoutIcon
              sx={{
                fontSize: 18,
                // Match the chat's "thinking" animation: gray while spinning,
                // brand blue when idle.
                color: isStreaming ? "#B0BEC5" : theme.palette.encampBlue.main,
                animation: isStreaming ? "scoutSpin 1.1s linear infinite" : "none",
                transformOrigin: "center",
              }}
            />
          </Box>
          {isStreaming ? (
            <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
              {activeToolLabel ?? "Thinking…"}
            </Typography>
          ) : null}
        </Box>
        <Tooltip title="Unminimize">
          <IconButton size="small" onClick={onExpand} aria-label="Unminimize Scout">
            <Remove fontSize="small" />
          </IconButton>
        </Tooltip>
        {fullPageHref ? (
          <Tooltip title="Open in full Scout">
            <IconButton
              component={RouterLink}
              to={fullPageHref}
              onClick={onClose}
              size="small"
              aria-label="Open conversation in full Scout"
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close Scout">
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
